/* eslint-disable import/no-cycle */
import { matchPath } from 'react-router-dom';

import PubSub from 'pubsub-js';

import oktaAuth from '../auth/oktaAuth';
import { getAppConfig, updateSessionStorage } from '.';
import { getCachedUserContext, getCachedUserInfo } from './common';
import {
  CART_DESC,
  CATALOG_EVENT,
  CATALOG_PDP_VIEW_PRODUCT_DESCRIPTION,
  CLICK_EVENT,
  CONTRACT_EVENT,
  DOCUMENTS_EVENT,
  DOCUMENTS_PRODUCT_PAGELOAD_DESCP,
  DOCUMENTS_TECHNICAL_PAGELOAD_DESCP,
  EXTERNAL_USER,
  GLOBAL_NOTIFICATIONS_ANALYTICS,
  INTERNAL_SEARCH_CAPABILITY,
  INTERNAL_USER,
  MY_REQUESTS_ANALYTICS,
  NOTIFICATIONS_PREFERENCES_ANALYTICS,
  ORDERS_EVENT,
  ORDERS_HISTORY_DETAILS_DESC,
  PAGE_LOAD_EVENT,
  REORDER_ANALYTICS,
  REQUEST_QUOTE_EVENT,
  REQUEST_SAMPLE,
  SEARCH_RESULTS_EVENT,
  SUPPORT_EVENT
} from './constants';

let pages;
let isLoggedIn;
let userContextDetails = null;

export const createAppEvent = () => {
  window.appEventData = [];
};

export const setAppEventValues = obj => {
  const redifinedOBJ = { ...obj };
  if (!window.appEventData) createAppEvent();
  if (
    redifinedOBJ.isTrusted !== false ||
    redifinedOBJ.isTrusted === undefined ||
    redifinedOBJ.description === DOCUMENTS_PRODUCT_PAGELOAD_DESCP ||
    redifinedOBJ.description === DOCUMENTS_TECHNICAL_PAGELOAD_DESCP
  ) {
    delete redifinedOBJ.isTrusted;
    window.appEventData.push(redifinedOBJ);
  }
};

export const setAccountAndRegionDetails = accountAndRegion => {
  const { accounts = '', regions = '' } = accountAndRegion;
  if (accounts.length || regions.length) {
    const findAccount = accounts.find(item => item.selected);
    const findRegion = regions.find(item => item.selected);
    return { accountName: `${findAccount.fullname}_${findRegion.shortname}`, sfid: findAccount.sfid };
  }
  return { accountName: '', sfid: '' };
};

export const setEvent = (pageData, eventName) => {
  if (pageData && pageData.event) return pageData.event;
  return eventName || 'Page Loaded';
};

const formatPermissions = groups =>
  groups
    .reduce((acc, group) => {
      if (group.toLowerCase().includes('dxp_food_') || group.toLowerCase().includes('dxp_mfa_')) {
        acc.push(group.toLowerCase().replace(/dxp_food_accgrp_|dxp_food_|dxp_/g, ''));
      }
      return acc;
    }, [])
    .join(',');

const getUserDetails = async data => {
  const accessToken = await oktaAuth.getAccessToken();
  const { payload } = await oktaAuth.token.decode(accessToken);

  const {
    data: { groups }
  } = await getCachedUserInfo();
  const permissions = formatPermissions(groups);
  const accountAndRegion = updateSessionStorage.getItem('accountAndRegion');
  const accountAndRegionDetails = setAccountAndRegionDetails(accountAndRegion);
  let customerType = EXTERNAL_USER;
  if (groups.includes('dxp_food_internalusers')) {
    customerType = INTERNAL_USER;
  }
  let accountName = accountAndRegionDetails?.accountName || '';
  let onboardingBusinessUnit = (userContextDetails?.onboardingBusiness || '').replaceAll(';', ',');

  // in case user is red carpet and does not have account or context setup yet
  if (groups.includes('dxp_onboarding_externalusers')) {
    if (!onboardingBusinessUnit) {
      onboardingBusinessUnit = 'Red Carpet';
    }
    if (!accountName) {
      accountName = 'Red Carpet Account';
    }
  }

  const externalAccountID = data.accountExternalID || '';
  const oktaID = payload.uid || '';
  const accountSFID = accountAndRegionDetails?.sfid || '';
  const accountSalesUnit = userContextDetails?.role || '';
  const customerTypes = (userContextDetails?.customerTypes || []).join(', ');
  const businessUnit = (userContextDetails?.businessUnit || '').replace(';', ',');
  const customerSegment = userContextDetails?.customerSegment || '';

  return {
    user: {
      externalAccountID,
      oktaID,
      type: customerType,
      accountSFID,
      permissions,
      accountSalesUnit,
      customerType: customerTypes,
      accountName,
      onboardingBusinessUnit,
      businessUnit,
      customerSegment
    }
  };
};

export const sendAnalyticsData = async (pageData, data = {}, eventName = '') => {
  let appEventObj = {};
  const defaultObj = {
    event: setEvent(pageData, eventName),
    description: data.description || pageData.description,
    experience: {
      experienceCategory: pageData.experienceCategory,
      experienceProduct: pageData.experienceProduct,
      capability: data.capability || pageData.capability,
      productRegion: userContextDetails?.region || 'NA',
      country: pageData.country,
      language: pageData.language
    },
    page: {
      pageName: data.pageName || pageData.pageName
    }
  };
  appEventObj = { ...appEventObj, ...defaultObj };
  if (isLoggedIn) {
    const updatedObj = await getUserDetails(data);
    appEventObj = { ...appEventObj, ...updatedObj };
  }
  if (Object.keys(data).length) {
    const values = { ...data };
    delete values.capability;
    delete values.pageName;
    delete values.accountExternalID;
    appEventObj = { ...appEventObj, ...values };
  }
  setAppEventValues(appEventObj);
};

const handleChange = (data = {}, eventName = '') => {
  const {
    location: { pathname }
  } = window;
  if (pages && pages.length) {
    for (let i = 0; i < pages.length; i += 1) {
      const pageData = pages[i];
      const { path: matchUrl } = pageData;
      const matchResult = matchPath(matchUrl, pathname);
      if (matchResult) {
        if (
          !(
            (pageData.description === CATALOG_PDP_VIEW_PRODUCT_DESCRIPTION ||
              pageData.description === ORDERS_HISTORY_DETAILS_DESC) &&
            !Object.keys(data).length
          )
        ) {
          if (!(eventName.length === 0 && pageData.description === CART_DESC && !data.product)) {
            sendAnalyticsData(pageData, data, eventName);
          }
        }
        break;
      }
    }
  }
};

const changeHandler = (_eventName, data) => {
  if (data) {
    handleChange(data, data.event);
  }
};

export const setAnalyticsEvents = async () => {
  PubSub.subscribe(SUPPORT_EVENT, changeHandler);

  PubSub.subscribe(CLICK_EVENT, changeHandler);

  PubSub.subscribe(CONTRACT_EVENT, changeHandler);

  PubSub.subscribe(ORDERS_EVENT, changeHandler);

  PubSub.subscribe(REQUEST_SAMPLE, changeHandler);

  PubSub.subscribe(REQUEST_QUOTE_EVENT, changeHandler);

  PubSub.subscribe(CATALOG_EVENT, changeHandler);

  PubSub.subscribe(DOCUMENTS_EVENT, changeHandler);

  PubSub.subscribe(NOTIFICATIONS_PREFERENCES_ANALYTICS, changeHandler);

  PubSub.subscribe(GLOBAL_NOTIFICATIONS_ANALYTICS, changeHandler);

  PubSub.subscribe(MY_REQUESTS_ANALYTICS, changeHandler);

  PubSub.subscribe(REORDER_ANALYTICS, changeHandler);

  PubSub.subscribe(PAGE_LOAD_EVENT, (_eventName, data = {}) => {
    handleChange(data);
  });

  PubSub.subscribe(SEARCH_RESULTS_EVENT, (_eventName, data) => {
    if (data) {
      const values = {
        ...data,
        capability: INTERNAL_SEARCH_CAPABILITY
      };
      handleChange(values, values.event);
    }
  });

  isLoggedIn = await oktaAuth.isAuthenticated();
  const analyticsData = getAppConfig('analyticsData');
  const { pages: pageData } = analyticsData;
  pages = pageData;

  if (isLoggedIn) {
    userContextDetails = await getCachedUserContext();
  }

  handleChange();
};
